/**
 * Centralized layout constants with explanations.
 *
 * Moving magic numbers here improves maintainability:
 * - Easy to find and update values
 * - Documentation explains WHY these values exist
 * - Can be referenced across layout code
 */

/**
 * Layout spacing and sizing constants.
 */
export const LAYOUT_CONSTANTS = {
  /**
   * Vertical padding for panel content (pixels).
   * Applied to top and bottom of the media panel.
   */
  TOP_AND_BOTTOM_PADDING: 25,

  /**
   * Horizontal gap between columns (pixels).
   * CSS grid-gap value for spacing tiles.
   */
  SPACE_BETWEEN_COLUMNS: 6,

  /**
   * Line height for media captions (pixels).
   * Used to calculate available height when captions are shown.
   */
  CAPTION_LINE_HEIGHT: 22,

  /**
   * Maximum images per page before pagination (gallery mode).
   *
   * This value ensures good performance while maintaining a
   * grid-looking UI. Calculated to fill ~4 rows with default
   * column count.
   */
  MAX_GALLERY_IMAGES: 32,

  /**
   * Maximum plotly charts per page before pagination.
   *
   * Plotly performs poorly with many charts. This limit
   * allows 2 panels to be open simultaneously with good
   * performance.
   */
  MAX_GALLERY_PLOTLY: 4,

  /**
   * Minimum column count allowed.
   */
  MIN_COL_COUNT: 1,

  /**
   * Maximum column count allowed.
   * Prevents layouts from becoming unusably small.
   */
  MAX_COL_COUNT: 12,
} as const;

/**
 * Default dimensions for different media types (pixels).
 */
export const MEDIA_DIMENSIONS = {
  /** Default height for audio players */
  AUDIO_HEIGHT: 100,

  /** Default card width fallback */
  DEFAULT_CARD_WIDTH: 300,

  /** Default card height fallback */
  DEFAULT_CARD_HEIGHT: 300,
} as const;

/**
 * Control placement rules.
 *
 * These define the default locations for controls based on
 * panel configuration.
 */
export const CONTROL_PLACEMENT = {
  /** Location for linked controls (affect all tiles) */
  LINKED_LOCATION: 'content-header' as const,

  /** Location for individual controls (per tile) */
  INDIVIDUAL_LOCATION: 'tile-header' as const,

  /** Location for fanned-out dimension labels */
  FANOUT_LOCATION: 'row-header' as const,
} as const;

/**
 * Performance thresholds.
 */
export const PERFORMANCE_THRESHOLDS = {
  /** Max tiles to render without pagination (images) */
  MAX_IMAGES_WITHOUT_PAGINATION: 32,

  /** Max tiles to render without pagination (plotly) */
  MAX_PLOTLY_WITHOUT_PAGINATION: 4,

  /** Max tiles to render without pagination (videos) */
  MAX_VIDEOS_WITHOUT_PAGINATION: 16,
} as const;
